import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import { media } from "../../server";
import { useStateContext } from "../../context/ContextProvider";

import Button from "../layout/Button";

import { IoArrowBackOutline } from "react-icons/io5";
import { FiStar, FiClock } from "react-icons/fi";

function FeaturedMealsCard({
  meals,
  viewMode = "grid",
}) {
  const { currentDir } = useStateContext();
  const { t } = useTranslation();

  const rating = 4.5;

  // =========================
  // Meal Data
  // =========================

  const title =
    currentDir === "ltr"
      ? meals?.titleEn
      : meals?.titleAr;

  const description =
    currentDir === "ltr"
      ? meals?.descriptionEn
      : meals?.descriptionAr;

  const categoryTitle =
    currentDir === "ltr"
      ? meals?.categoryId?.titleEn
      : meals?.categoryId?.titleAr;

  const preparationTime =
    meals?.preparationTime
      ? `${meals.preparationTime} ${t("minutes")}`
      : `15-20 ${t("minutes")}`;

  // =========================
  // Price
  // =========================

  const hasPrice =
    meals?.price !== undefined &&
    meals?.price !== null &&
    meals?.price !== "";

  const formattedPrice = hasPrice
    ? Number(meals.price).toLocaleString(
        currentDir === "rtl"
          ? "ar-EG"
          : "en-US"
      )
    : null;

  // =========================
  // Scroll Top
  // =========================

  const handleScrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // LIST VIEW
  // =========================================================

  if (viewMode === "list") {
    return (
      <motion.article
        whileHover={{ y: -4 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 24,
        }}
        className="
          group
          relative
          flex
          flex-col
          sm:flex-row
          overflow-hidden
          rounded-3xl
          border
          border-gray-100
          dark:border-n-6
          bg-n-1
          dark:bg-n-7
          shadow-lg
          transition-all
          duration-500
          hover:shadow-2xl
        "
      >
        {/* ================= Image ================= */}

        <div
          className="
            relative
            w-full
            h-56
            sm:w-56
            md:w-64
            sm:h-auto
            shrink-0
            overflow-hidden
          "
        >
          <img
            src={`${media}${meals?.Image}`}
            alt={title}
            loading="lazy"
            className="
              w-full
              h-full
              object-cover
              transition-transform
              duration-700
              group-hover:scale-110
            "
          />

          {/* Overlay */}
          <div
            className="
              absolute
              inset-0
              bg-gradient-to-t
              from-black/40
              via-transparent
              to-transparent
            "
          />

          {/* Popular Badge */}
          <div
            className="
              absolute
              top-4
              start-4
              max-w-[85%]
              rounded-full
              bg-color-1
              px-3
              py-1.5
              text-xs
              text-white
              font-kufi
              shadow-lg
            "
          >
            <span className="truncate">
              {categoryTitle || t("popular")}
            </span>
          </div>
        </div>

        {/* ================= Content ================= */}

        <div
          className="
            flex
            flex-1
            flex-col
            justify-between
            p-5
            md:p-7
          "
        >
          <div>
            {/* Title + Rating */}

            <div
              className="
                mb-3
                flex
                items-start
                justify-between
                gap-4
              "
            >
              <h3
                className="
                  h6
                  line-clamp-2
                  text-n-8
                  dark:text-white
                  transition-colors
                  group-hover:text-color-1
                "
              >
                {title}
              </h3>

              <div
                className="
                  shrink-0
                  flex
                  items-center
                  gap-1.5
                  rounded-full
                  bg-color-1/10
                  px-3
                  py-1.5
                "
              >
                <FiStar
                  size={14}
                  className="
                    text-yellow-500
                    fill-yellow-500
                  "
                />

                <span
                  className="
                    text-sm
                    font-semibold
                    text-n-8
                    dark:text-white
                  "
                >
                  {rating}
                </span>
              </div>
            </div>

            {/* Description */}

            <p
              className="
                mb-4
                line-clamp-2
                text-sm
                text-n-5
                dark:text-n-3
              "
            >
              {description}
            </p>

            {/* Time */}

            <div
              className="
                flex
                items-center
                gap-2
                text-xs
                text-n-4
                dark:text-n-4
              "
            >
              <FiClock size={13} />

              <span
                className="
                  font-kufi
                  font-light
                "
              >
                {preparationTime}
              </span>
            </div>
          </div>

          {/* ================= Bottom ================= */}

          <div
            className="
              mt-6
              flex
              flex-col
              gap-4
              sm:flex-row
              sm:items-end
              sm:justify-between
            "
          >
            {/* Price */}

            <div className="min-w-0">
              {hasPrice ? (
                <div
                  className="
                    flex
                    flex-wrap
                    items-center
                    gap-x-3
                    gap-y-2
                  "
                >
                  {/* Main Price */}

                  <div
                    className="
                      flex
                      items-end
                      gap-1.5
                    "
                  >
                    <span
                      className="
                        text-2xl
                        md:text-3xl
                        font-bold
                        leading-none
                        text-color-1
                      "
                    >
                      {formattedPrice}
                    </span>

                    <span
                      className="
                        mb-0.5
                        font-kufi
                        text-sm
                        text-color-1
                      "
                    >
                      {t("pounds")}
                    </span>
                  </div>

                  {/* Tax Note */}

                  <span
                    className="
                      inline-flex
                      items-center
                      whitespace-nowrap
                      rounded-full
                      border
                      border-color-1/15
                      bg-color-1/5
                      px-2.5
                      py-1
                      font-kufi
                      text-[10px]
                      md:text-xs
                      leading-5
                      text-n-5
                      dark:border-color-1/20
                      dark:bg-color-1/10
                      dark:text-n-3
                    "
                  >
                    {t("priceExcludesTax")}
                  </span>
                </div>
              ) : (
                <span
                  className="
                    font-kufi
                    text-sm
                    text-color-1
                  "
                >
                  {t("today price")}
                </span>
              )}
            </div>

            {/* Details Button */}

            <Link
              to={`/FeaturedMealDetails/${meals?._id}`}
              onClick={handleScrollTop}
              className="shrink-0"
            >
              <Button
                text={t("More Details")}
                Transform={true}
                nonBackground={true}
                textStyle="font-kufi text-sm text-simeBold"
                textColor="text-color-1"
                icon={
                  <IoArrowBackOutline
                    className={
                      currentDir === "ltr"
                        ? "rotate-180"
                        : ""
                    }
                  />
                }
              />
            </Link>
          </div>
        </div>
      </motion.article>
    );
  }

  // =========================================================
  // GRID VIEW
  // =========================================================

  return (
    <motion.article
      whileHover={{ y: -8 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 24,
      }}
      className="
        group
        relative
        flex
        h-full
        flex-col
        overflow-hidden
        rounded-2xl
        md:rounded-3xl
        border
        border-gray-100
        dark:border-n-6
        bg-n-1
        dark:bg-n-7
        shadow-lg
        transition-all
        duration-500
        hover:shadow-2xl
      "
    >
      {/* ================= Image ================= */}

      <div
        className="
          relative
          h-64
          shrink-0
          overflow-hidden
        "
      >
        <img
          src={`${media}${meals?.Image}`}
          alt={title}
          loading="lazy"
          className="
            w-full
            h-full
            object-cover
            transition-transform
            duration-700
            group-hover:scale-110
          "
        />

        {/* Overlay */}

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-t
            from-black/40
            via-transparent
            to-black/5
          "
        />

        {/* Category */}

        <div
          className="
            absolute
            top-4
            start-4
            max-w-[80%]
          "
        >
          <span
            className="
              block
              truncate
              rounded-full
              bg-color-1
              px-3
              py-1.5
              font-kufi
              text-xs
              text-white
              shadow-lg
            "
          >
            {categoryTitle || t("popular")}
          </span>
        </div>

        {/* Rating */}

        <div
          className="
            absolute
            bottom-4
            end-4
            flex
            items-center
            gap-1
            rounded-full
            bg-black/60
            px-2.5
            py-1.5
            text-white
            backdrop-blur-sm
          "
        >
          <FiStar
            size={12}
            className="
              fill-yellow-500
              text-yellow-500
            "
          />

          <span
            className="
              text-xs
              font-medium
            "
          >
            {rating}
          </span>
        </div>
      </div>

      {/* ================= Content ================= */}

      <div
        className="
          flex
          flex-1
          flex-col
          p-5
        "
      >
        {/* Title */}

        <h3
          className="
            h6
            line-clamp-1
            text-n-8
            dark:text-white
            transition-colors
            group-hover:text-color-1
          "
        >
          {title}
        </h3>

        {/* Time */}

        <div
          className="
            mt-2
            flex
            items-center
            gap-2
            text-xs
            text-n-4
            dark:text-n-4
          "
        >
          <FiClock size={12} />

          <span className="font-kufi">
            {preparationTime}
          </span>
        </div>

        {/* Description */}

        <p
          className="
            mt-3
            line-clamp-2
            text-sm
            text-n-5
            dark:text-n-3
          "
        >
          {description}
        </p>

        {/* =================================================
            Price + Button
        ================================================= */}

        <div
          className="
            mt-auto
            flex
            items-end
            justify-between
            gap-3
            pt-5
          "
        >
          {/* ================= Price ================= */}

          <div className="min-w-0 flex-1">
            {hasPrice ? (
              <div
                className="
                  flex
                  flex-col
                  items-start
                  gap-1.5
                "
              >
                {/* Price */}

                <div
                  className="
                    flex
                    items-end
                    gap-1.5
                  "
                >
                  <span
                    className="
                      text-2xl
                      font-bold
                      leading-none
                      text-color-1
                    "
                  >
                    {formattedPrice}
                  </span>

                  <span
                    className="
                      mb-0.5
                      font-kufi
                      text-xs
                      text-color-1
                    "
                  >
                    {t("pounds")}
                  </span>
                </div>

                {/* Tax Note */}

                <span
                  className="
                    inline-flex
                    max-w-full
                    items-center
                    whitespace-nowrap
                    rounded-full
                    border
                    border-color-1/15
                    bg-color-1/5
                    px-2
                    py-0.5
                    font-kufi
                    text-[9px]
                    md:text-[10px]
                    leading-5
                    text-n-5
                    dark:border-color-1/20
                    dark:bg-color-1/10
                    dark:text-n-3
                  "
                >
                  {t("priceExcludesTax")}
                </span>
              </div>
            ) : (
              <span
                className="
                  font-kufi
                  text-sm
                  text-color-1
                "
              >
                {t("today price")}
              </span>
            )}
          </div>

          {/* ================= Button ================= */}

          <Link
            to={`/FeaturedMealDetails/${meals?._id}`}
            onClick={handleScrollTop}
            className="shrink-0"
          >
            <Button
              text={t("More Details")}
              Transform={true}
              nonBackground={true}
              textStyle="font-kufi text-sm text-simeBold"
              textColor="text-color-1"
              icon={
                <IoArrowBackOutline
                  className={
                    currentDir === "ltr"
                      ? "rotate-180"
                      : ""
                  }
                />
              }
            />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

export default FeaturedMealsCard;